import './AddTray.scss'
import { useNavigate } from 'react-router-dom'
import TrayId from './TrayId/TrayId'
import AddTrayDetails from './AddTrayDetails/AddTrayDetails'
import SubTrays from './SubTrays/SubTrays'
import InventorySheets from './InventorySheets/InventorySheets'
import AssignStep from './AssignStep/AssignStep'
import Review from './Review/Review'
import { useUser } from 'app/User'
import AlertDialog from 'components/AlertDialog/AlertDialog'
import { useAddTrayContext } from './AddTray.context'
import { createContext, useContext, useState } from 'react'
import { Step } from './AddTray.types'
import ParentTrayImage from './ParentTrayImage/ParentTrayImage'
import { useSubTraysContext } from './SubTrays/SubTrays.context'
import TrayList from './TrayList/TrayList'

const TrayStepContext = createContext<
  ReturnType<typeof useAddTrayStepper> | undefined
>(undefined)

export const useTrayStepContext = () => {
  const context = useContext(TrayStepContext)
  if (!context) {
    throw new Error('useTrayStepContext must be used within an AddTrayProvider')
  }
  return context
}

const useAddTrayStepper = () => {
  const navigate = useNavigate()

  const { isGuest } = useUser()
  const { hasSubTrays, isFixingRejectedTray, selectedTrayType } =
    useAddTrayContext()
  const { currentView } = useSubTraysContext()

  const isCustomImplantTray = selectedTrayType === 'custom implants'

  const [stepNumber, setStepNumber] = useState<number>(0)
  const [highestStepReached, setHighestStepReached] = useState<number>(0)
  const [isDiscardAlertOpen, setIsDiscardAlertOpen] = useState<boolean>(false)

  const stepMap: Step[] = [
    {
      label: 'Tray ID',
      Component: TrayId,
      guidance:
        'Generate your tray ID, this ID will be used for tracking and management purposes.',
    },
    {
      label: 'Tray Details',
      Component: AddTrayDetails,
      guidance:
        'Fill in the essential information about your tray including description, vendor, and weight.',
    },
    {
      label: 'Parent Tray Image',
      Component: ParentTrayImage,
      guidance:
        'Capture a clear reference image of the parent tray with the lid removed.',
    },
    {
      label: hasSubTrays ? 'Add Trays' : 'Add Tray',
      customGuideLabel: 'Build Your Tray',
      Component: SubTrays,
      guidance: hasSubTrays
        ? isFixingRejectedTray
          ? 'Review and update the sub-trays that will be included within this parent tray.'
          : isCustomImplantTray
          ? 'Open the individual bags with the custom products and load your tray with all your custom products and then capture a photo of the fully loaded tray.  Save your UDI barcode/matrix to register on the next step'
          : 'Add implant sub-tray(s) or instrument sub-tray(s) / levels of tray.'
        : currentView === 'list'
        ? 'Select the tray type and add the details for this single tray.'
        : isCustomImplantTray
        ? 'Open the individual bags with the custom products and load your tray with all your custom products and then capture a photo of the fully loaded tray.  Save your UDI barcode/matrix to register on the next step'
        : 'Take photo to digitize your tray',
    },
    {
      label: 'Supporting Documents',
      Component: InventorySheets,
      guidance:
        'You may upload IFU, inventory sheets or any other supporting documents.',
    },
    {
      label: 'Assign',
      Component: AssignStep,
      guidance:
        'Assign this tray to specific surgeons, procedures, or departments as needed.',
    },
    ...(!isFixingRejectedTray
      ? [
          {
            label: 'Tray List',
            Component: TrayList,
            guidance: 'Manage multiple trays for this case',
          },
        ]
      : []),
    {
      label: 'Review',
      Component: Review,
      guidance:
        'Review all information before finalizing. You can go back to previous steps to make changes if needed.',
    },
  ]
  const steps = stepMap.filter((step) => !step.skipForSingleTray || hasSubTrays)
  const currentStep = steps[stepNumber]

  const nextStep = () => {
    const nextStepNumber = stepNumber + 1
    setStepNumber(nextStepNumber)
    setHighestStepReached(Math.max(highestStepReached, nextStepNumber))
  }

  const prevStep = () => setStepNumber(stepNumber - 1)

  const reset = () => {
    setStepNumber(0)
    setHighestStepReached(0)
  }

  const goToStep = (step: number) => {
    if (step <= highestStepReached) {
      setStepNumber(step)
    }
  }

  const handleDiscardAndExit = () => {
    setIsDiscardAlertOpen(true)
  }

  const handleDiscardConfirm = () => {
    reset()
    if (isGuest) {
      navigate('/guest-info')
    } else {
      navigate('/tray-management')
    }
    setIsDiscardAlertOpen(false)
  }

  const handleDiscardCancel = () => {
    setIsDiscardAlertOpen(false)
  }

  return {
    stepNumber,
    steps,
    currentStep,
    nextStep,
    prevStep,
    reset,
    goToStep,
    highestStepReached,
    handleDiscardAndExit,
    isDiscardAlertOpen,
    handleDiscardConfirm,
    handleDiscardCancel,
  }
}

export const TrayStepProvider: React.FC = ({ children }) => {
  const value = useAddTrayStepper()
  return (
    <TrayStepContext.Provider value={value}>
      {children}
      <AlertDialog
        isOpen={value.isDiscardAlertOpen}
        mode="warning"
        title="Discard Changes"
        description="Exiting will discard all your current progress. Are you sure that you want to exit?"
        secondaryButtonText="Cancel"
        secondaryButtonAction={value.handleDiscardCancel}
        primaryButtonText="Exit"
        primaryButtonAction={value.handleDiscardConfirm}
        zIndex={999}
      />
    </TrayStepContext.Provider>
  )
}
